import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import {
    ArrowDownTrayIcon,
    ClipboardDocumentIcon,
    XMarkIcon,
    MagnifyingGlassPlusIcon,
    MagnifyingGlassMinusIcon,
    ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { setNotifications } from '../../../../../redux/features/app/appSlice';
import { useTheme } from '../../../../../theme/ThemeContext';
import { NOTIFICATION_TEMPLATES } from '../../Notifications/NotificationConstants';

interface ImageOverlayProps {
    open: boolean;
    onClose: () => void;
    imageSrc?: string;
    svgContent?: string;
    altText?: string;
}

export const ImageOverlay: React.FC<ImageOverlayProps> = ({
    open,
    onClose,
    imageSrc,
    svgContent,
    altText = "Image"
}) => {
    const { theme } = useTheme();
    const dispatch = useDispatch();
    const [isSaving, setIsSaving] = React.useState(false);
    const [isCopying, setIsCopying] = React.useState(false);
    const [zoom, setZoom] = React.useState(1);
    const [pan, setPan] = React.useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = React.useState(false);
    const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });
    const imageContainerRef = React.useRef<HTMLDivElement>(null);

    // Process SVG content to ensure it scales properly in the overlay
    const processedSvgContent = React.useMemo(() => {
        if (!svgContent) return '';

        // Remove fixed width/height attributes and ensure SVG scales properly
        let processed = svgContent;

        // Replace fixed width/height with viewBox if not present
        processed = processed.replace(/<svg([^>]*?)>/i, (_match, attrs) => {
            // Remove width and height attributes but preserve viewBox
            let newAttrs = attrs;
            newAttrs = newAttrs.replace(/\s*width\s*=\s*["'][^"']*["']/gi, '');
            newAttrs = newAttrs.replace(/\s*height\s*=\s*["'][^"']*["']/gi, '');

            // Ensure we have proper scaling attributes
            if (!newAttrs.includes('viewBox')) {
                // If no viewBox, try to extract from the original width/height
                const widthMatch = attrs.match(/width\s*=\s*["']([^"']*)["']/i);
                const heightMatch = attrs.match(/height\s*=\s*["']([^"']*)["']/i);

                if (widthMatch && heightMatch) {
                    const width = widthMatch[1].replace(/[^\d.]/g, '');
                    const height = heightMatch[1].replace(/[^\d.]/g, '');
                    if (width && height) {
                        newAttrs += ` viewBox="0 0 ${width} ${height}"`;
                    }
                }
            }

            // Add scaling attributes
            newAttrs += ' width="100%" height="100%" preserveAspectRatio="xMidYMid meet"';

            return `<svg${newAttrs}>`;
        });

        return processed;
    }, [svgContent]);

    // Convert SVG to PNG blob for saving/copying at display size
    const svgToPng = React.useCallback(async (svgString: string): Promise<Blob> => {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                reject(new Error('Failed to get canvas context'));
                return;
            }

            // Parse SVG to get dimensions
            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');
            const svgElement = svgDoc.querySelector('svg');

            if (!svgElement) {
                reject(new Error('Failed to parse SVG'));
                return;
            }

            // Try to get the actual rendered size from the DOM
            const overlayDiv = document.querySelector('.image-overlay-svg-content');
            let width = 1600; // Increased default fallback
            let height = 1200; // Increased default fallback

            if (overlayDiv) {
                const rect = overlayDiv.getBoundingClientRect();

                if (rect.width > 0 && rect.height > 0) {
                    // Use the actual display size, scaled up significantly for better quality
                    const scaleFactor = 3; // Increased scale factor
                    width = Math.round(rect.width * scaleFactor);
                    height = Math.round(rect.height * scaleFactor);
                } else {
                    // Overlay has zero dimensions, using fallback
                }
            } else {
                // Enhanced fallback: get viewBox or default dimensions, but scale them up significantly
                const viewBox = svgElement.getAttribute('viewBox');

                if (viewBox) {
                    const [, , w, h] = viewBox.split(' ').map(Number);
                    width = Math.max((w || 800) * 2, 1600); // Ensure minimum size
                    height = Math.max((h || 600) * 2, 1200);
                } else {
                    const widthAttr = svgElement.getAttribute('width');
                    const heightAttr = svgElement.getAttribute('height');
                    if (widthAttr && heightAttr) {
                        width = Math.max((parseInt(widthAttr.replace(/\D/g, ''), 10) || 800) * 2, 1600);
                        height = Math.max((parseInt(heightAttr.replace(/\D/g, ''), 10) || 600) * 2, 1200);
                    }
                }
            }

            canvas.width = width;
            canvas.height = height;

            // Set background color based on theme for better mermaid export
            // Only add white background in light mode - dark mode SVGs already have their own background
            if (theme === 'light') {
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, width, height);
            }

            // Create an image element and set crossOrigin to avoid tainted canvas
            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = () => {
                try {
                    // Calculate the proper dimensions to maintain aspect ratio
                    const imgAspectRatio = img.width / img.height;
                    const canvasAspectRatio = width / height;

                    let drawWidth, drawHeight, drawX, drawY;

                    if (imgAspectRatio > canvasAspectRatio) {
                        // Image is wider - fit to width
                        drawWidth = width;
                        drawHeight = width / imgAspectRatio;
                        drawX = 0;
                        drawY = (height - drawHeight) / 2;
                    } else {
                        // Image is taller - fit to height
                        drawHeight = height;
                        drawWidth = height * imgAspectRatio;
                        drawX = (width - drawWidth) / 2;
                        drawY = 0;
                    }

                    ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
                    canvas.toBlob((blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error('Failed to convert canvas to blob'));
                        }
                    }, 'image/png');
                } catch (error) {
                    reject(new Error('Failed to draw image to canvas: ' + error));
                }
            };

            img.onerror = () => reject(new Error('Failed to load SVG image'));

            // Create SVG data URL directly without blob to avoid cross-origin issues
            const svgDataUrl = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
            img.src = svgDataUrl;
        });
    }, [theme]);

    // Convert WebP to PNG blob for clipboard compatibility at display size
    const webpToPng = React.useCallback(async (webpDataUrl: string): Promise<Blob> => {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            if (!ctx) {
                reject(new Error('Failed to get canvas context'));
                return;
            }

            // Set crossOrigin to avoid tainted canvas
            img.crossOrigin = 'anonymous';

            img.onload = () => {
                try {
                    // Try to get the actual rendered size from the DOM
                    const overlayImg = document.querySelector('.image-overlay-img-content');
                    let width = img.width * 2; // Default to 2x original size
                    let height = img.height * 2;

                    if (overlayImg) {
                        const rect = overlayImg.getBoundingClientRect();

                        if (rect.width > 0 && rect.height > 0) {
                            // Use the actual display size, scaled up significantly for better quality
                            const scaleFactor = 3; // Increased scale factor
                            width = Math.round(rect.width * scaleFactor);
                            height = Math.round(rect.height * scaleFactor);
                        }
                    }

                    // Ensure minimum size
                    width = Math.max(width, 800);
                    height = Math.max(height, 600);

                    canvas.width = width;
                    canvas.height = height;

                    // Calculate the proper dimensions to maintain aspect ratio
                    const imgAspectRatio = img.width / img.height;
                    const canvasAspectRatio = width / height;

                    let drawWidth, drawHeight, drawX, drawY;

                    if (imgAspectRatio > canvasAspectRatio) {
                        // Image is wider - fit to width
                        drawWidth = width;
                        drawHeight = width / imgAspectRatio;
                        drawX = 0;
                        drawY = (height - drawHeight) / 2;
                    } else {
                        // Image is taller - fit to height
                        drawHeight = height;
                        drawWidth = height * imgAspectRatio;
                        drawX = (width - drawWidth) / 2;
                        drawY = 0;
                    }

                    ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
                    canvas.toBlob((blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error('Failed to convert canvas to blob'));
                        }
                    }, 'image/png');
                } catch (error) {
                    reject(new Error('Failed to draw image to canvas: ' + error));
                }
            };

            img.onerror = () => reject(new Error('Failed to load WebP image'));
            img.src = webpDataUrl;
        });
    }, []);

    const handleSaveImage = React.useCallback(async () => {
        setIsSaving(true);
        const startTime = Date.now();

        try {
            if (svgContent) {
                // For SVG content (like Mermaid diagrams), convert to PNG
                const blob = await svgToPng(processedSvgContent);
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `diagram_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.png`;
                link.click();
                URL.revokeObjectURL(url);
            } else if (imageSrc) {
                // For WebP or other image formats, always save as PNG
                if (imageSrc.includes('data:image/webp')) {
                    const blob = await webpToPng(imageSrc);
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `image_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.png`;
                    link.click();
                    URL.revokeObjectURL(url);
                } else {
                    // For other formats, convert to PNG using canvas
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    const img = new Image();

                    if (!ctx) {
                        throw new Error('Failed to get canvas context');
                    }

                    await new Promise((resolve, reject) => {
                        img.crossOrigin = 'anonymous';

                        img.onload = () => {
                            try {
                                // Try to get the actual rendered size from the DOM
                                const overlayImg = document.querySelector('.image-overlay-img-content');
                                let width = img.width * 2; // Default to 2x original size
                                let height = img.height * 2;

                                if (overlayImg) {
                                    const rect = overlayImg.getBoundingClientRect();

                                    if (rect.width > 0 && rect.height > 0) {
                                        // Use the actual display size, scaled up significantly for better quality
                                        const scaleFactor = 3; // Increased scale factor
                                        width = Math.round(rect.width * scaleFactor);
                                        height = Math.round(rect.height * scaleFactor);
                                    }
                                }

                                // Ensure minimum size
                                width = Math.max(width, 800);
                                height = Math.max(height, 600);

                                canvas.width = width;
                                canvas.height = height;

                                // Calculate the proper dimensions to maintain aspect ratio
                                const imgAspectRatio = img.width / img.height;
                                const canvasAspectRatio = width / height;

                                let drawWidth, drawHeight, drawX, drawY;

                                if (imgAspectRatio > canvasAspectRatio) {
                                    // Image is wider - fit to width
                                    drawWidth = width;
                                    drawHeight = width / imgAspectRatio;
                                    drawX = 0;
                                    drawY = (height - drawHeight) / 2;
                                } else {
                                    // Image is taller - fit to height
                                    drawHeight = height;
                                    drawWidth = height * imgAspectRatio;
                                    drawX = (width - drawWidth) / 2;
                                    drawY = 0;
                                }

                                ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
                                canvas.toBlob((blob) => {
                                    if (blob) {
                                        const url = URL.createObjectURL(blob);
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.download = `image_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.png`;
                                        link.click();
                                        URL.revokeObjectURL(url);
                                        resolve(undefined);
                                    } else {
                                        reject(new Error('Failed to convert to PNG'));
                                    }
                                }, 'image/png');
                            } catch (error) {
                                reject(new Error('Failed to draw image to canvas: ' + error));
                            }
                        };

                        img.onerror = () => reject(new Error('Failed to load image'));
                        img.src = imageSrc;
                    });
                }
            }
        } catch (error) {
            console.error('Failed to save image:', error);
        } finally {
            // Ensure loading state is visible for at least 500ms for better UX
            const elapsedTime = Date.now() - startTime;
            const minDuration = 500;
            const remainingTime = Math.max(0, minDuration - elapsedTime);

            setTimeout(() => {
                setIsSaving(false);
            }, remainingTime);
        }
    }, [svgContent, imageSrc, processedSvgContent, svgToPng, webpToPng]);

    const handleCopyImage = React.useCallback(async () => {
        setIsCopying(true);
        const startTime = Date.now();

        try {
            if (svgContent) {
                // For SVG content, convert to PNG for clipboard
                const blob = await svgToPng(processedSvgContent);
                await navigator.clipboard.write([
                    new ClipboardItem({
                        'image/png': blob,
                    }),
                ]);
            } else if (imageSrc) {
                // For WebP, convert to PNG for clipboard compatibility
                if (imageSrc.includes('data:image/webp')) {
                    const blob = await webpToPng(imageSrc);
                    await navigator.clipboard.write([
                        new ClipboardItem({
                            'image/png': blob,
                        }),
                    ]);
                } else {
                    // For other formats, try to copy directly or convert to PNG
                    const response = await fetch(imageSrc);
                    const blob = await response.blob();
                    if (blob.type === 'image/png' || blob.type === 'image/jpeg') {
                        await navigator.clipboard.write([
                            new ClipboardItem({
                                [blob.type]: blob,
                            }),
                        ]);
                    } else {
                        // Convert to PNG for clipboard compatibility
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        const img = new Image();

                        await new Promise((resolve, reject) => {
                            if (!ctx) {
                                reject(new Error('Failed to get canvas context'));
                                return;
                            }

                            img.crossOrigin = 'anonymous';

                            img.onload = () => {
                                try {
                                    // Try to get the actual rendered size from the DOM
                                    const overlayImg = document.querySelector('.image-overlay-img-content');
                                    let width = img.width * 2; // Default to 2x original size
                                    let height = img.height * 2;

                                    if (overlayImg) {
                                        const rect = overlayImg.getBoundingClientRect();

                                        if (rect.width > 0 && rect.height > 0) {
                                            // Use the actual display size, scaled up significantly for better quality
                                            const scaleFactor = 3; // Increased scale factor
                                            width = Math.round(rect.width * scaleFactor);
                                            height = Math.round(rect.height * scaleFactor);
                                        }
                                    }

                                    // Ensure minimum size
                                    width = Math.max(width, 800);
                                    height = Math.max(height, 600);

                                    canvas.width = width;
                                    canvas.height = height;

                                    // Calculate the proper dimensions to maintain aspect ratio
                                    const imgAspectRatio = img.width / img.height;
                                    const canvasAspectRatio = width / height;

                                    let drawWidth, drawHeight, drawX, drawY;

                                    if (imgAspectRatio > canvasAspectRatio) {
                                        // Image is wider - fit to width
                                        drawWidth = width;
                                        drawHeight = width / imgAspectRatio;
                                        drawX = 0;
                                        drawY = (height - drawHeight) / 2;
                                    } else {
                                        // Image is taller - fit to height
                                        drawHeight = height;
                                        drawWidth = height * imgAspectRatio;
                                        drawX = (width - drawWidth) / 2;
                                        drawY = 0;
                                    }

                                    ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
                                    canvas.toBlob(async (pngBlob) => {
                                        if (pngBlob) {
                                            await navigator.clipboard.write([
                                                new ClipboardItem({
                                                    'image/png': pngBlob,
                                                }),
                                            ]);
                                            resolve(undefined);
                                        } else {
                                            reject(new Error('Failed to convert to PNG'));
                                        }
                                    }, 'image/png');
                                } catch (error) {
                                    reject(new Error('Failed to draw image to canvas: ' + error));
                                }
                            };

                            img.onerror = () => reject(new Error('Failed to load image'));
                            img.src = imageSrc;
                        });
                    }
                }
            }

            // Show success notification
            dispatch(setNotifications([{ ...NOTIFICATION_TEMPLATES.COPY_PROMPT_CHATIMAGE }]));
        } catch (error) {
            console.error('Failed to copy image to clipboard:', error);
        } finally {
            // Ensure loading state is visible for at least 500ms for better UX
            const elapsedTime = Date.now() - startTime;
            const minDuration = 500;
            const remainingTime = Math.max(0, minDuration - elapsedTime);

            setTimeout(() => {
                setIsCopying(false);
            }, remainingTime);
        }
    }, [svgContent, imageSrc, processedSvgContent, svgToPng, webpToPng, dispatch]);

    // Zoom and pan handlers
    const handleWheel = React.useCallback((e: React.WheelEvent) => {
        e.preventDefault();
        const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
        const newZoom = Math.min(Math.max(zoom * zoomFactor, 0.1), 5);
        setZoom(newZoom);
    }, [zoom]);

    const handleMouseDown = React.useCallback((e: React.MouseEvent) => {
        if (zoom > 1) {
            setIsDragging(true);
            setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
        }
    }, [zoom, pan]);

    const handleMouseMove = React.useCallback((e: React.MouseEvent) => {
        if (isDragging && zoom > 1) {
            setPan({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    }, [isDragging, dragStart, zoom]);

    const handleMouseUp = React.useCallback(() => {
        setIsDragging(false);
    }, []);

    const handleZoomIn = React.useCallback(() => {
        const newZoom = Math.min(zoom * 1.2, 5);
        setZoom(newZoom);
    }, [zoom]);

    const handleZoomOut = React.useCallback(() => {
        const newZoom = Math.max(zoom * 0.8, 0.1);
        setZoom(newZoom);
        // Reset pan if zoomed out to fit
        if (newZoom <= 1) {
            setPan({ x: 0, y: 0 });
        }
    }, [zoom]);

    const handleResetZoom = React.useCallback(() => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    }, []);

    // Close on escape key
    React.useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && open) {
                onClose();
            }
        };

        if (open) {
            document.addEventListener('keydown', handleEscape);
            // Prevent body scrolling when overlay is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <Dialog open={open} onClose={onClose} className="relative z-50">
            <DialogBackdrop
                transition
                className={`fixed inset-0 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in ${
                    theme === 'dark' ? 'bg-black/90' : 'bg-white/90'
                }`}
            />
            <div className="fixed inset-0 z-10 w-screen overflow-hidden">
                <div className="flex min-h-full items-center justify-center p-4">
                    <DialogPanel
                        transition
                        className="relative w-full h-full max-w-none max-h-none flex flex-col items-center justify-center transition-all data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in p-4"
                    >
                        {/* Image content */}
                        <div
                            ref={imageContainerRef}
                            className="flex-1 w-full flex items-center justify-center cursor-pointer pb-20 sm:pb-24 overflow-hidden"
                            onClick={() => {
                                // Only close if not zoomed and not dragging
                                if (zoom <= 1 && !isDragging) {
                                    onClose();
                                }
                            }}
                            onWheel={handleWheel}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'pointer' }}
                        >
                            {imageSrc ? (
                                <img
                                    src={imageSrc}
                                    alt={altText}
                                    className="image-overlay-img-content transition-transform duration-200 select-none"
                                    style={{
                                        maxWidth: zoom <= 1 ? '90vw' : 'none',
                                        maxHeight: zoom <= 1 ? 'calc(100vh - 120px)' : 'none',
                                        width: 'auto',
                                        height: 'auto',
                                        objectFit: 'contain',
                                        transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                                        transformOrigin: 'center center',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (zoom <= 1 && !isDragging) {
                                            onClose();
                                        }
                                    }}
                                    onDragStart={(e) => e.preventDefault()}
                                />
                            ) : svgContent ? (
                                <div
                                    className="image-overlay-svg-content w-full h-full flex items-center justify-center transition-transform duration-200 select-none"
                                    dangerouslySetInnerHTML={{ __html: processedSvgContent }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (zoom <= 1 && !isDragging) {
                                            onClose();
                                        }
                                    }}
                                    style={{
                                        maxWidth: zoom <= 1 ? '90vw' : 'none',
                                        maxHeight: zoom <= 1 ? 'calc(100vh - 120px)' : 'none',
                                        minWidth: zoom <= 1 ? '50vw' : 'none',
                                        minHeight: zoom <= 1 ? '50vh' : 'none',
                                        transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                                        transformOrigin: 'center center',
                                    }}
                                />
                            ) : null}
                        </div>

                        {/* Zoom controls - positioned on the right side */}
                        <div className="fixed right-4 top-1/2 transform -translate-y-1/2 z-[70] flex flex-col gap-2 bg-black/20 backdrop-blur-sm rounded-full p-2">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleZoomIn();
                                }}
                                className={`p-2 rounded-full transition-colors ${
                                    theme === 'dark'
                                        ? 'text-white/80 hover:text-white bg-black/20 hover:bg-black/40'
                                        : 'text-gray-700 hover:text-gray-900 bg-white/80 hover:bg-white shadow-md border border-gray-200'
                                }`}
                                aria-label="Zoom in"
                                title="Zoom in"
                            >
                                <MagnifyingGlassPlusIcon className="size-5" />
                            </button>

                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleZoomOut();
                                }}
                                className={`p-2 rounded-full transition-colors ${
                                    theme === 'dark'
                                        ? 'text-white/80 hover:text-white bg-black/20 hover:bg-black/40'
                                        : 'text-gray-700 hover:text-gray-900 bg-white/80 hover:bg-white shadow-md border border-gray-200'
                                }`}
                                aria-label="Zoom out"
                                title="Zoom out"
                            >
                                <MagnifyingGlassMinusIcon className="size-5" />
                            </button>

                            {zoom !== 1 && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleResetZoom();
                                    }}
                                    className={`p-2 rounded-full transition-colors ${
                                        theme === 'dark'
                                            ? 'text-white/80 hover:text-white bg-black/20 hover:bg-black/40'
                                            : 'text-gray-700 hover:text-gray-900 bg-white/80 hover:bg-white shadow-md border border-gray-200'
                                    }`}
                                    aria-label="Reset zoom"
                                    title="Reset zoom"
                                >
                                    <ArrowsPointingOutIcon className="size-5" />
                                </button>
                            )}
                        </div>

                        {/* Action buttons - fixed position to always be visible */}
                        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-[70] flex justify-center items-center gap-3 sm:gap-4 bg-black/20 backdrop-blur-sm rounded-full px-4 py-2 sm:px-6 sm:py-3">
                            {/* Save Image button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSaveImage();
                                }}
                                disabled={isSaving}
                                className={`p-2 sm:p-3 rounded-full transition-colors disabled:opacity-50 ${
                                    theme === 'dark'
                                        ? 'text-white/80 hover:text-white bg-black/20 hover:bg-black/40'
                                        : 'text-gray-700 hover:text-gray-900 bg-white/80 hover:bg-white shadow-md border border-gray-200'
                                }`}
                                aria-label="Save image as PNG"
                                title="Save image as PNG"
                            >
                                {isSaving ? (
                                    <div className={`size-5 sm:size-6 animate-spin rounded-full border-2 ${
                                        theme === 'dark'
                                            ? 'border-white/20 border-t-white/80'
                                            : 'border-gray-300 border-t-gray-700'
                                    }`} />
                                ) : (
                                    <ArrowDownTrayIcon className="size-5 sm:size-6" />
                                )}
                            </button>

                            {/* Copy to Clipboard button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopyImage();
                                }}
                                disabled={isCopying}
                                className={`p-2 sm:p-3 rounded-full transition-colors disabled:opacity-50 ${
                                    theme === 'dark'
                                        ? 'text-white/80 hover:text-white bg-black/20 hover:bg-black/40'
                                        : 'text-gray-700 hover:text-gray-900 bg-white/80 hover:bg-white shadow-md border border-gray-200'
                                }`}
                                aria-label="Copy image to clipboard"
                                title="Copy image to clipboard"
                            >
                                {isCopying ? (
                                    <div className={`size-5 sm:size-6 animate-spin rounded-full border-2 ${
                                        theme === 'dark'
                                            ? 'border-white/20 border-t-white/80'
                                            : 'border-gray-300 border-t-gray-700'
                                    }`} />
                                ) : (
                                    <ClipboardDocumentIcon className="size-5 sm:size-6" />
                                )}
                            </button>

                            {/* Close button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onClose();
                                }}
                                className={`p-2 sm:p-3 rounded-full transition-colors ${
                                    theme === 'dark'
                                        ? 'text-white/80 hover:text-white bg-black/20 hover:bg-black/40'
                                        : 'text-gray-700 hover:text-gray-900 bg-white/80 hover:bg-white shadow-md border border-gray-200'
                                }`}
                                aria-label="Close image overlay"
                                title="Close"
                            >
                                <XMarkIcon className="size-5 sm:size-6" />
                            </button>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
};
